/**
 * Result Type
 *
 * A type-safe way to represent operations that can succeed or fail.
 * Inspired by Rust's Result<T, E> type.
 *
 * Use this instead of throwing exceptions in domain/service logic.
 */

import type { AppError } from "./errors";

/**
 * Result type for operations that can fail
 *
 * @template T - The type of the success value
 * @template E - The type of the error (defaults to AppError)
 */
export type Result<T, E = AppError> =
  | { ok: true; value: T }
  | { ok: false; error: E };

/**
 * Helper to create success results
 *
 * @example
 * return Ok({ id: '123', name: 'John' });
 */
export function Ok<T>(value: T): Result<T, never> {
  return { ok: true, value };
}

/**
 * Helper to create error results
 *
 * @example
 * return Err({ type: 'NotFound', resource: 'user', id: userId });
 */
export function Err<E>(error: E): Result<never, E> {
  return { ok: false, error };
}

/**
 * Check if a result is successful
 *
 * @example
 * if (isOk(result)) {
 *   console.log(result.value); // TypeScript knows this is safe
 * }
 */
export function isOk<T, E>(
  result: Result<T, E>
): result is { ok: true; value: T } {
  return result.ok === true;
}

/**
 * Check if a result is an error
 *
 * @example
 * if (isErr(result)) {
 *   handleError(result.error); // TypeScript knows this is safe
 * }
 */
export function isErr<T, E>(
  result: Result<T, E>
): result is { ok: false; error: E } {
  return result.ok === false;
}

/**
 * Map a successful result value to a new value
 * If the result is an error, return it unchanged
 *
 * @example
 * const result = await getUserProfile(userId);
 * const displayNameResult = mapResult(result, user => user.displayName);
 */
export function mapResult<T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => U
): Result<U, E> {
  if (result.ok) {
    return Ok(fn(result.value));
  }
  return result;
}

/**
 * Chain operations that return Results
 * If the first result is an error, return it unchanged
 *
 * @example
 * const result = await getUserProfile(userId);
 * const validatedResult = andThen(result, user => validateUser(user));
 */
export function andThen<T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => Result<U, E>
): Result<U, E> {
  if (result.ok) {
    return fn(result.value);
  }
  return result;
}

/**
 * Provide a default value if result is an error
 *
 * @example
 * const name = unwrapOr(result, 'Guest');
 */
export function unwrapOr<T, E>(result: Result<T, E>, defaultValue: T): T {
  if (result.ok) {
    return result.value;
  }
  return defaultValue;
}

/**
 * Get the value from a result, throwing the error if it failed
 * Use sparingly - prefer explicit error handling
 *
 * @example
 * const user = unwrap(result); // throws if result is error
 */
export function unwrap<T, E>(result: Result<T, E>): T {
  if (result.ok) {
    return result.value;
  }
  throw result.error;
}
